// TopicSelectorPage — simplified Phase 3
// Pick teaching form + topic → create session → start learning.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { useSessions } from '../../hooks/useSessions';
import { getTopicsByJourney } from '../../services/firebase';
import Button from '../ui/Button';
import Layout from '../layout/Layout';
import type { TeachingForm } from '../../types';

interface TopicItem {
  id: string;
  title: string;
  subtopics: { id: string; title: string }[];
  journeyId: string;
}

const FORMS: TeachingForm[] = ['introduction', 'dialog', 'exam'];

const TopicSelectorPage: React.FC = () => {
  const { mode } = useParams<{ mode: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('session');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { journeys, loading: journeysLoading } = useLearningJourneys();
  const { createNewSession } = useSessions();

  // Map legacy mode names to new form names
  const mapToForm = (m: string | undefined): TeachingForm => {
    const map: Record<string, TeachingForm> = {
      introduction: 'introduction',
      education: 'dialog',
      repetition: 'dialog',
      training: 'exam',
      dialog: 'dialog',
      exam: 'exam',
    };
    return map[m || ''] || 'introduction';
  };

  const [selectedForm, setSelectedForm] = useState<TeachingForm>(mapToForm(mode));
  const [topics, setTopics] = useState<TopicItem[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState(searchParams.get('journeyId') || '');
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch topics from journeys
  useEffect(() => {
    if (!user?.uid || journeys.length === 0) {
      setTopics([]);
      return;
    }

    const fetchTopics = async () => {
      setTopicsLoading(true);
      const all: TopicItem[] = [];

      for (const journey of journeys) {
        try {
          const result = await getTopicsByJourney(journey.id);
          if (result.success && result.topics) {
            for (const t of result.topics) {
              all.push({
                id: t.id,
                title: t.title,
                subtopics: (t.subtopics || []).map((s, i) => ({
                  id: typeof s === 'object' && s !== null && 'id' in s ? (s as { id: string }).id : `sub_${i}`,
                  title: typeof s === 'string' ? s : (s as { title: string }).title,
                })),
                journeyId: journey.id,
              });
            }
          }
        } catch {
          // Skip failed journey
        }
      }

      setTopics(all);
      setTopicsLoading(false);
    };

    fetchTopics();
  }, [user?.uid, journeys]);

  const filteredTopics = selectedJourneyId
    ? topics.filter(t => t.journeyId === selectedJourneyId)
    : topics;

  const selectedTopic = topics.find(t => t.id === selectedTopicId);

  const handleStartSession = async () => {
    if (!selectedTopicId || !selectedTopic || isCreating) return;
    setIsCreating(true);

    const result = await createNewSession({
      topicId: selectedTopicId,
      topicTitle: selectedTopic.title,
      subtopicIds: selectedTopic.subtopics.map(s => s.id),
      teachingForm: selectedForm,
      learningJourneyId: selectedTopic.journeyId,
      goals: [],
    });

    if (!result.success || !result.id) {
      console.error('[TopicSelectorPage] Failed to create session:', result.error);
      setIsCreating(false);
      return;
    }

    // Go directly to voice session (skip briefing)
    navigate(`/session/${result.id}`);
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/sessions')}
            className="text-sm text-neutral-medium hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-4 flex items-center gap-1"
          >
            <span>&larr;</span> {t('setup.backToTeaching')}
          </button>
          <h1 className="text-2xl font-display font-semibold text-neutral-dark dark:text-white">
            {t('setup.title')}
          </h1>
        </div>

        {/* Teaching form picker */}
        <div className="mb-6">
          <p className="text-sm font-medium text-neutral-medium mb-3">{t('setup.pickForm')}</p>
          <div className="grid grid-cols-3 gap-2">
            {FORMS.map(form => (
              <button
                key={form}
                onClick={() => setSelectedForm(form)}
                className={`p-3 rounded-xl text-center transition-all border-2 ${
                  selectedForm === form
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="text-2xl block mb-1">{t(`forms.${form}.emoji`)}</span>
                <span className="text-sm font-medium text-neutral-dark dark:text-white block">
                  {t(`forms.${form}.title`)}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-sm text-neutral-medium">
            {t(`forms.${selectedForm}.description`)}
          </p>
        </div>

        {/* Journey filter */}
        {journeys.length > 1 && (
          <div className="mb-6">
            <select
              value={selectedJourneyId}
              onChange={(e) => {
                setSelectedJourneyId(e.target.value);
                setSelectedTopicId(null);
              }}
              className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-800 text-neutral-dark dark:text-gray-200 border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500 focus:outline-none focus:ring-2 focus:ring-opacity-50"
              aria-label={t('setup.filterByJourney')}
            >
              <option value="">{t('setup.allTopics')}</option>
              {journeys.map(j => (
                <option key={j.id} value={j.id}>{j.title || j.subject}</option>
              ))}
            </select>
          </div>
        )}

        {/* Topics */}
        {journeysLoading || topicsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : filteredTopics.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-4xl mb-4">📚</p>
            <h3 className="text-lg font-semibold text-neutral-dark dark:text-white mb-2">
              {t('setup.noTopics')}
            </h3>
            <p className="text-neutral-medium mb-4">{t('setup.noTopicsDescription')}</p>
            <Button variant="primary" onClick={() => navigate('/library')}>
              {t('setup.goToLibrary')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium text-neutral-medium">{t('setup.pickTopic')}</p>
            {filteredTopics.map(topic => (
              <button
                key={topic.id}
                onClick={() => setSelectedTopicId(topic.id === selectedTopicId ? null : topic.id)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  selectedTopicId === topic.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                }`}
              >
                <h3 className="font-semibold text-neutral-dark dark:text-white">
                  {topic.title}
                </h3>
                {selectedTopicId === topic.id && topic.subtopics.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-neutral-medium mb-1">{t('setup.agenda')}</p>
                    <ol className="list-decimal list-inside space-y-0.5">
                      {topic.subtopics.map(sub => (
                        <li key={sub.id} className="text-sm text-neutral-dark dark:text-white">
                          {sub.title}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Start button */}
        {selectedTopicId && (
          <div className="mt-8 flex justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleStartSession}
              disabled={isCreating}
            >
              {isCreating ? t('page.ending') : t('setup.startSession')}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TopicSelectorPage;
